import { RocketDepositPoolAbi, RocketRethAbi } from '../abis/rocketpool'
import { TotalSupplyAbi } from '../abis/shared'
import type { LstRegistryEntry } from '../registry'
import { ONE_E18, toBigInt, type LstCall, type LstReader } from './shared'

/**
 * Rocket Pool rETH reader.
 *
 * Two required calls: `rETH.totalSupply()` + `rETH.getExchangeRate()`.
 * Optional third call: `RocketDepositPool.getBalance()` — instant-burn
 * buffer. Added when the registry's `readerExtras.rocketDepositPool`
 * is populated (which it is for chainId 1).
 *
 * Note: the deposit-pool address upgrades via `RocketStorage` over
 * time; the registry holds the current snapshot, but production code
 * paths that mint should resolve via `RocketStorage` rather than
 * hard-coding. Reading the balance for display is more forgiving.
 */
export const readerRocketReth = (entry: LstRegistryEntry): LstReader => {
  const depositPool = entry.readerExtras?.rocketDepositPool
  const calls: LstCall[] = [
    { address: entry.address, name: 'totalSupply', params: [] },
    { address: entry.address, name: 'getExchangeRate', params: [] },
  ]
  const abis: any[] = [TotalSupplyAbi, RocketRethAbi]
  if (depositPool) {
    calls.push({ address: depositPool, name: 'getBalance', params: [] })
    abis.push(RocketDepositPoolAbi)
  }
  return {
    calls,
    abis,
    parse: (slice) => {
      const totalSupply = toBigInt(slice[0])
      const exchangeRate = toBigInt(slice[1])
      if (totalSupply === undefined || exchangeRate === undefined) {
        return undefined
      }
      const liquidity = depositPool ? toBigInt(slice[2]) : undefined
      return {
        totalAssets: (totalSupply * exchangeRate) / ONE_E18,
        totalSupply,
        exchangeRate,
        liquidity,
      }
    },
  }
}
